import type { ReactNode } from "react";
import type { ButtonProps } from "react-aria-components";
import { Button as ReactAriaButton } from "react-aria-components";

import type { IcIcon } from "@jetblue/kit-icons/types";

import { ICON_BUTTON_STYLES } from "./icon-button-styles";
import type { IconButtonSize, IconButtonVariant } from "./icon-button-types";
import { cn } from "../../utilities/tailwind";

export type IconButtonProperties = {
  /**
   * Accepts an icon as a function (from our icon library, e.g. `IcClose`).
   */
  Icon?: IcIcon | undefined;

  /**
   * Defines a string value that labels the current element.
   */
  "aria-label": string;

  /**
   * If you do not want to use the default stylings applied to an icon by the component, you may pass an icon as a child to the `IconButton`.
   */
  children?: ReactNode;

  /**
   * Class name to apply to the button stylings
   */
  className?: string | undefined;

  /**
   * Sizes available to the `IconButton`.
   */
  size?: IconButtonSize | undefined;

  /**
   * Standardized stylings available to the `IconButton`.
   */
  variant?: IconButtonVariant | undefined;
} & Omit<
  ButtonProps,
  "aria-label" | "children" | "className" | "isPending" | "style" | "slot"
>;
export const IconButton = ({
  Icon,
  className = "",
  size = "small",
  variant = "primary",
  ...properties
}: IconButtonProperties) => {
  const isXSStandard = variant === "standard" && size === "extraSmall";
  const isXSStandardDisabled = properties.isDisabled && isXSStandard;

  const twClassName = cn(
    ICON_BUTTON_STYLES.base,
    ICON_BUTTON_STYLES.color[variant],
    ICON_BUTTON_STYLES.size[size],
    {
      [ICON_BUTTON_STYLES.extraSmallStandard.disabled]: isXSStandardDisabled,
    },
    ICON_BUTTON_STYLES.disabled,
    className,
  );
  const twClassNameInner = cn("flex items-center justify-center", {
    [ICON_BUTTON_STYLES.extraSmallStandard.border]: isXSStandard,
    "border-medium-gray": isXSStandardDisabled,
  });

  return (
    <ReactAriaButton className={twClassName} data-size={size} {...properties}>
      <div className={twClassNameInner}>
        {Icon && (
          <Icon
            aria-hidden
            className={ICON_BUTTON_STYLES.iconSize[size]}
            role="img"
          />
        )}

        {properties.children ?? null}
      </div>
    </ReactAriaButton>
  );
};
IconButton.displayName = "IconButton";
